import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import {
  X, ChevronLeft, ChevronRight, Heart, MessageCircle, ExternalLink,
  Play, Pause, Volume2, VolumeX, Loader2, Instagram, Unlink, RefreshCw,
  CheckCircle2, XCircle, Users, Image, Clock, LayoutGrid, Settings, Plus, Link2,
  Film, Star, ShieldCheck, AlertTriangle,
} from 'lucide-react';

/* ═══ TYPES ═══ */
interface Post {
  id: number; media_url: string; thumbnail_url: string | null; video_url: string | null;
  instagram_url: string | null; description: string | null;
  media_type: 'IMAGE' | 'VIDEO' | 'CAROUSEL_ALBUM'; source_type: string;
  like_count: number; comments_count: number;
  instagram_post_id: string | null; instagram_timestamp: string | null;
  created_at: string; embed_html: string | null;
  carousel_media: CarouselItem[] | null;
}
interface CarouselItem {
  media_url: string | null; video_url: string | null;
  is_video: boolean; media_type: 'IMAGE' | 'VIDEO';
}
interface StoryItem {
  id: number; story_id: string; media_type: 'IMAGE' | 'VIDEO';
  media_url: string | null; video_url: string | null; thumbnail_url: string | null;
  proxy_url: string | null; caption: string | null; link_url: string | null;
  story_timestamp: string | null; expiry_timestamp: string | null;
  duration_seconds: number | null; has_audio: boolean;
  is_active_on_ig: boolean; is_archived: boolean; is_downloaded: boolean;
  is_highlight: boolean; highlight_id: string | null; highlight_title: string | null;
  width: number | null; height: number | null; created_at: string | null;
}
interface InstagramAccount {
  id: number; username: string | null; profile_picture_url: string | null;
  followers_count: number; media_count: number;
  sync_enabled: number; is_active: number; last_synced_at: string | null;
}
interface AccountResponse {
  account: InstagramAccount | null; is_connected: boolean;
  posts_count: number; stories_count: number; active_stories_count: number;
  puppeteer_available: boolean;
}
interface Insights {
  reach?: number;
  profile_views?: number;
  likes?: number;
  comments?: number;
  shares?: number;
  saves?: number;
}
interface OAuthAccount {
  status: 'connected' | 'expired' | 'not_connected';
  username?: string;
  profile_picture_url?: string | null;
  followers_count?: number;
  media_count?: number;
  posts_count?: number;
  expires_at?: string | null;
  days_left?: number | null;
  last_synced_at?: string | null;
  insights?: Insights | null;
}
// keep OAuthStatus as alias for backward compat
type OAuthStatus = OAuthAccount;

/* ═══ HELPERS ═══ */
const timeAgo = (d: string | null | undefined): string => {
  if (!d) return '';
  const diff = Date.now() - new Date(d).getTime();
  const m = Math.floor(diff / 60000);
  if (m < 1) return 'now'; if (m < 60) return `${m}m`; const h = Math.floor(m / 60);
  if (h < 24) return `${h}h`; const dd = Math.floor(h / 24);
  if (dd < 7) return `${dd}d`; return `${Math.floor(dd / 7)}w`;
};
const fmt = (n: number | null | undefined): string => { if (!n) return '0'; if (n >= 1e6) return `${(n / 1e6).toFixed(1)}M`; if (n >= 1e3) return `${(n / 1e3).toFixed(1)}K`; return String(n); };
const fmtDate = (d: string | null): string => {
  if (!d) return 'Never';
  return new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' });
};
const fixUrl = (u: string | null | undefined, apiBase: string): string | null => {
  if (!u) return null;
  if (u.startsWith('/api/') || u.startsWith('/uploads/')) {
    try { return `${new URL(apiBase).origin}${u}`; } catch { return u; }
  }
  return u;
};

/**
 * Safely extract a URL string from a value that might be a raw Instagram
 * GraphImage/GraphVideo object instead of a plain string.
 * The API sometimes returns the full IG node object for carousel items.
 */
const safeUrl = (val: unknown): string | null => {
  if (!val) return null;
  if (typeof val === 'string') return val;
  if (typeof val === 'object' && val !== null) {
    const obj = val as Record<string, unknown>;
    return (obj.display_url as string) || (obj.video_url as string) || null;
  }
  return null;
};

/* ═══ EMBED COMPONENT ═══ */
declare global { interface Window { instgrm?: { Embeds: { process: (el?: HTMLElement | null) => void } }; } }
const InstagramEmbed: React.FC<{ embedHtml: string | null; permalink: string | null }> = ({ embedHtml, permalink }) => {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!window.instgrm) {
      const s = document.querySelector('script[src*="instagram.com/embed.js"]');
      if (!s) { const sc = document.createElement('script'); sc.src = 'https://www.instagram.com/embed.js'; sc.async = true; document.body.appendChild(sc); }
    }
    const t = setTimeout(() => window.instgrm?.Embeds?.process(ref.current), 800);
    return () => clearTimeout(t);
  }, [embedHtml]);
  if (embedHtml) return <div ref={ref} dangerouslySetInnerHTML={{ __html: embedHtml }} className="w-full flex justify-center" style={{ minHeight: 300 }} />;
  return <a href={permalink || '#'} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-gray-700 hover:text-gray-900 p-4 transition-colors"><Instagram size={20} /><span>View on Instagram</span><ExternalLink size={14} /></a>;
};

/* ═══════════════════════════════════════════════════════════════
   MAIN COMPONENT
   ═══════════════════════════════════════════════════════════════ */
const SchoolInstagram: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState<'posts' | 'stories' | 'account'>('posts');
  const [oauthResult, setOauthResult] = useState<{ status: string; message: string } | null>(null);
  const INSTA_API_BASE = import.meta.env.VITE_API_URL || 'https://crm-api.yellowslate.com/api';

  // Detect OAuth callback redirect — e.g. ?instagram_oauth=success&message=...
  useEffect(() => {
    const oauthParam = searchParams.get('instagram_oauth');
    if (oauthParam) {
      const message = searchParams.get('message') || '';
      setOauthResult({ status: oauthParam, message });
      setActiveTab('account');
      // Clean the URL params without a page reload
      setSearchParams({}, { replace: true });
    }
  }, []);

  return (
    <div>
      {oauthResult && (
        <div className={`mb-4 flex items-start gap-2.5 p-3 rounded-lg border-2 ${oauthResult.status === 'success' ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}`}>
          {oauthResult.status === 'success'
            ? <CheckCircle2 size={16} className="text-green-500 mt-0.5 shrink-0" />
            : <XCircle size={16} className="text-red-500 mt-0.5 shrink-0" />}
          <p className={`text-sm ${oauthResult.status === 'success' ? 'text-green-700' : 'text-red-700'}`}>
            {oauthResult.status === 'success' ? 'Instagram connected successfully via Official API.' : oauthResult.message}
          </p>
          <button onClick={() => setOauthResult(null)} className="ml-auto text-gray-400 hover:text-gray-700"><X size={14} /></button>
        </div>
      )}
      <div className="flex items-center gap-1 p-1 bg-gray-100 rounded-xl mb-5 w-fit">
        {([
          { key: 'posts' as const, label: 'Posts', Icon: LayoutGrid },
          { key: 'stories' as const, label: 'Stories', Icon: Film },
          { key: 'account' as const, label: 'Account', Icon: Settings },
        ]).map(t => (
          <button key={t.key} onClick={() => setActiveTab(t.key)}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${activeTab === t.key ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}>
            <t.Icon size={15} /> {t.label}
          </button>
        ))}
      </div>
      {activeTab === 'posts' && <PostsTab slateId={id!} apiBase={INSTA_API_BASE} />}
      {activeTab === 'stories' && <StoriesTab slateId={id!} apiBase={INSTA_API_BASE} />}
      {activeTab === 'account' && <AccountTab slateId={id!} apiBase={INSTA_API_BASE} oauthConnected={oauthResult?.status === 'success'} />}
    </div>
  );
};
export default SchoolInstagram;

/* ═══════════════════════════════════════════════════════════════
   POSTS TAB — Card Grid
   API: GET /instastories/school/:slate_id?limit=30
   ═══════════════════════════════════════════════════════════════ */
const PostsTab: React.FC<{ slateId: string; apiBase: string }> = ({ slateId, apiBase }) => {
  const [posts, setPosts] = useState<Post[]>([]);
  const [igUser, setIgUser] = useState<string | null>(null);
  const [pfpUrl, setPfpUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [activeIdx, setActiveIdx] = useState<number | null>(null);
  const [progress, setProgress] = useState(0);
  const [paused, setPaused] = useState(false);
  const [muted, setMuted] = useState(true);
  const [showCap, setShowCap] = useState(false);
  const [mediaOk, setMediaOk] = useState(false);
  const [imgErr, setImgErr] = useState<Record<number, boolean>>({});
  const [carouselIdx, setCarouselIdx] = useState(0);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const vidRef = useRef<HTMLVideoElement | null>(null);

  useEffect(() => {
    if (!slateId) return;
    (async () => {
      try {
        setLoading(true);
        const r = await fetch(`${apiBase}/instastories/school/${slateId}?limit=100`);
        const j = await r.json();
        if (j.success && j.data?.stories?.length) {
          setPosts(j.data.stories.map((s: Post) => ({
            ...s,
            media_url: fixUrl(safeUrl(s.media_url), apiBase) || s.media_url,
            thumbnail_url: fixUrl(safeUrl(s.thumbnail_url), apiBase) || s.thumbnail_url,
            video_url: fixUrl(safeUrl(s.video_url), apiBase) || s.video_url,
            carousel_media: s.carousel_media?.map((c: CarouselItem) => {
              // Normalize: media_url/video_url may be raw IG GraphImage objects
              const mediaStr = safeUrl(c.media_url);
              const videoStr = safeUrl(c.video_url);
              return {
                ...c,
                media_url: fixUrl(mediaStr, apiBase) || mediaStr,
                video_url: fixUrl(videoStr, apiBase) || videoStr,
              };
            }) || null,
          })));
          setIgUser(j.data.instagram_username);
          setPfpUrl(fixUrl(j.data.profile_picture_url, apiBase));
        }
      } catch (e) { console.error(e); } finally { setLoading(false); }
    })();
  }, [slateId, apiBase]);

  const clearT = useCallback(() => { if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; } }, []);
  const startT = useCallback(() => {
    clearT(); if (activeIdx === null) return; const s = posts[activeIdx]; if (!s) return;
    const hasMedia = s.media_url && s.media_url.length > 10;
    if (!hasMedia && s.embed_html) return;
    const dur = s.media_type === 'VIDEO' ? 15000 : 6000;
    timerRef.current = setInterval(() => {
      setProgress(p => { const n = p + (30 / dur) * 100; if (n >= 100) { setActiveIdx(i => (i === null ? null : i < posts.length - 1 ? i + 1 : null)); return 0; } return n; });
    }, 30);
  }, [activeIdx, posts, clearT]);

  useEffect(() => { if (activeIdx !== null && !paused) { setMediaOk(false); setShowCap(false); setProgress(0); setCarouselIdx(0); startT(); } return clearT; }, [activeIdx, startT, clearT]);
  useEffect(() => { if (paused) clearT(); else if (activeIdx !== null) startT(); }, [paused, activeIdx, clearT, startT]);
  useEffect(() => { if (activeIdx === null) return; const h = (e: KeyboardEvent) => { if (e.key === 'Escape') close(); if (e.key === 'ArrowLeft') prev(); if (e.key === 'ArrowRight') next(); if (e.key === ' ') { e.preventDefault(); setPaused(p => !p); } }; window.addEventListener('keydown', h); return () => window.removeEventListener('keydown', h); }, [activeIdx, posts.length]);
  useEffect(() => { document.body.style.overflow = activeIdx !== null ? 'hidden' : ''; return () => { document.body.style.overflow = ''; }; }, [activeIdx]);

  const open = (i: number) => { setActiveIdx(i); setProgress(0); setPaused(false); setShowCap(false); };
  const close = () => { setActiveIdx(null); setProgress(0); setPaused(false); clearT(); };
  const next = () => { if (activeIdx === null) return; if (activeIdx < posts.length - 1) setActiveIdx(activeIdx + 1); else close(); setProgress(0); };
  const prev = () => { if (activeIdx === null || activeIdx <= 0) return; setActiveIdx(activeIdx - 1); setProgress(0); };
  const thumb = (s: Post) => s.thumbnail_url || s.media_url;

  if (loading) return <div className="py-6"><div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">{Array.from({ length: 8 }).map((_, i) => <div key={i} className="aspect-square rounded-xl bg-gray-100 animate-pulse border-2 border-gray-200" />)}</div></div>;
  if (!posts.length) return <div className="py-8 text-center"><div className="w-16 h-16 rounded-2xl mx-auto mb-4 flex items-center justify-center border-2 border-gray-300 bg-transparent"><Instagram size={28} className="text-gray-400" /></div><p className="text-sm font-medium text-gray-500">No Posts Yet</p><p className="text-xs text-gray-400 mt-1">Connect an account in the Account tab</p></div>;

  const as = activeIdx !== null ? posts[activeIdx] : null;

  return (
    <>
      <div className="py-5">
        <div className="flex items-center justify-between mb-4 px-1">
          <div className="flex items-center gap-2.5">
            <LayoutGrid size={18} className="text-gray-900" />
            <h3 className="text-[15px] font-semibold text-gray-900">Posts</h3>
            <span className="text-xs text-gray-400 font-medium">{posts.length} posts</span>
          </div>
          {igUser && <a href={`https://instagram.com/${igUser}`} target="_blank" rel="noopener noreferrer" className="text-xs text-gray-500 hover:text-gray-900 font-medium flex items-center gap-1 transition-colors">@{igUser}<ExternalLink size={11} /></a>}
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
          {posts.map((s, i) => (
            <button key={s.id} onClick={() => open(i)} className="group relative aspect-square rounded-xl overflow-hidden border-2 border-gray-200 hover:border-gray-900 transition-all bg-gray-50">
              {imgErr[s.id] || !thumb(s) ? <div className="w-full h-full flex items-center justify-center bg-gray-100"><Instagram size={28} className="text-gray-300" /></div>
              : <img src={thumb(s)} alt="" loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" onError={() => setImgErr(p => ({ ...p, [s.id]: true }))} />}
              <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-all flex items-center justify-center opacity-0 group-hover:opacity-100">
                <div className="flex items-center gap-4 text-white">
                  <span className="flex items-center gap-1.5"><Heart size={16} fill="white" /><span className="text-sm font-semibold">{fmt(s.like_count)}</span></span>
                  {s.comments_count > 0 && <span className="flex items-center gap-1.5"><MessageCircle size={15} fill="white" /><span className="text-sm font-semibold">{fmt(s.comments_count)}</span></span>}
                </div>
              </div>
              {s.media_type === 'VIDEO' && <div className="absolute top-2 right-2 w-6 h-6 bg-white/90 rounded-md flex items-center justify-center shadow-sm border border-gray-200"><Play size={11} className="text-gray-700 ml-[1px]" fill="currentColor" /></div>}
              {s.media_type === 'CAROUSEL_ALBUM' && <div className="absolute top-2 right-2 w-6 h-6 bg-white/90 rounded-md flex items-center justify-center shadow-sm border border-gray-200"><LayoutGrid size={10} className="text-gray-700" /></div>}
              <div className="absolute bottom-0 left-0 right-0 px-2.5 py-2 bg-gradient-to-t from-black/60 to-transparent"><span className="text-[10px] text-white/80 font-medium">{timeAgo(s.instagram_timestamp || s.created_at)}</span></div>
            </button>
          ))}
        </div>
      </div>

      {/* Fullscreen viewer */}
      {as && activeIdx !== null && (
        <div className="fixed inset-0 z-[99999] bg-black/95 flex items-center justify-center">
          <div className="absolute inset-0 hidden md:block" onClick={close} />
          <div className="relative w-full h-full md:w-[420px] md:h-[94vh] md:max-h-[820px] md:rounded-2xl overflow-hidden bg-black flex flex-col z-10">
            <div className="absolute top-0 left-0 right-0 z-30 flex gap-[3px] px-3 pt-3">{posts.map((_, i) => <div key={i} className="flex-1 h-[2.5px] bg-white/25 rounded-full overflow-hidden"><div className="h-full bg-white rounded-full" style={{ width: i < (activeIdx ?? 0) ? '100%' : i === activeIdx ? `${progress}%` : '0%' }} /></div>)}</div>
            <div className="absolute top-7 left-0 right-0 z-30 flex items-center justify-between px-4 py-1">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-full border-2 border-white/30 overflow-hidden bg-white/10 flex items-center justify-center flex-shrink-0">{pfpUrl ? <img src={pfpUrl} alt="" className="w-full h-full object-cover" /> : <span className="text-white text-sm font-bold">{igUser?.charAt(0).toUpperCase() || 'S'}</span>}</div>
                <div><p className="text-white text-sm font-semibold truncate drop-shadow-md">{igUser ? `@${igUser}` : 'School'}</p><p className="text-white/60 text-xs">{timeAgo(as.instagram_timestamp || as.created_at)} ago</p></div>
              </div>
              <div className="flex items-center gap-1">
                {as.media_type === 'VIDEO' && <button onClick={() => { setMuted(m => !m); if (vidRef.current) vidRef.current.muted = !muted; }} className="w-8 h-8 flex items-center justify-center text-white/80 hover:text-white">{muted ? <VolumeX size={18} /> : <Volume2 size={18} />}</button>}
                <button onClick={() => setPaused(p => !p)} className="w-8 h-8 flex items-center justify-center text-white/80 hover:text-white">{paused ? <Play size={18} /> : <Pause size={18} />}</button>
                <button onClick={close} className="w-8 h-8 flex items-center justify-center text-white/80 hover:text-white"><X size={20} /></button>
              </div>
            </div>
            <div className="flex-1 flex items-center justify-center relative select-none" onMouseDown={() => setPaused(true)} onMouseUp={() => setPaused(false)} onTouchStart={() => setPaused(true)} onTouchEnd={() => setPaused(false)}>
              {!mediaOk && <div className="absolute inset-0 flex items-center justify-center z-20"><Loader2 size={36} className="text-white/40 animate-spin" /></div>}
              {as.media_type === 'CAROUSEL_ALBUM' && as.carousel_media?.length ? (() => {
                const slide = as.carousel_media[carouselIdx]; const total = as.carousel_media.length;
                return (<div className="relative w-full h-full flex items-center justify-center">
                  {slide?.is_video || slide?.media_type === 'VIDEO' ? <video ref={vidRef} key={`${as.id}-${carouselIdx}`} src={slide.video_url || slide.media_url || ''} className="w-full h-full object-contain" autoPlay muted={muted} playsInline onLoadedData={() => setMediaOk(true)} /> : <img key={`${as.id}-${carouselIdx}`} src={slide?.media_url || as.media_url} alt="" className={`w-full h-full object-contain transition-opacity ${mediaOk ? 'opacity-100' : 'opacity-0'}`} onLoad={() => setMediaOk(true)} onError={() => setMediaOk(true)} />}
                  <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-1.5 z-20">{as.carousel_media.map((_, ci) => <div key={ci} className={`w-1.5 h-1.5 rounded-full transition-all ${ci === carouselIdx ? 'bg-white w-2.5' : 'bg-white/40'}`} />)}</div>
                  {carouselIdx > 0 && <button onClick={e => { e.stopPropagation(); setCarouselIdx(c => c - 1); setMediaOk(false); }} className="absolute left-2 top-1/2 -translate-y-1/2 w-8 h-8 bg-black/40 hover:bg-black/60 rounded-full flex items-center justify-center text-white z-20"><ChevronLeft size={18} /></button>}
                  {carouselIdx < total - 1 && <button onClick={e => { e.stopPropagation(); setCarouselIdx(c => c + 1); setMediaOk(false); }} className="absolute right-2 top-1/2 -translate-y-1/2 w-8 h-8 bg-black/40 hover:bg-black/60 rounded-full flex items-center justify-center text-white z-20"><ChevronRight size={18} /></button>}
                  <div className="absolute top-3 right-3 px-2.5 py-1 bg-black/50 rounded-full text-white text-[11px] font-medium z-20">{carouselIdx + 1}/{total}</div>
                </div>);
              })()
              : as.media_type === 'VIDEO' ? <video ref={vidRef} key={as.id} src={as.video_url || as.media_url} className="w-full h-full object-contain" autoPlay muted={muted} playsInline onLoadedData={() => setMediaOk(true)} />
              : as.media_url ? <div className="relative w-full h-full flex items-center justify-center"><img key={as.id} src={as.media_url} alt="" className={`w-full h-full object-contain transition-opacity ${mediaOk ? 'opacity-100' : 'opacity-0'}`} onLoad={() => setMediaOk(true)} onError={e => { setMediaOk(true); (e.target as HTMLImageElement).style.display = 'none'; const fb = (e.target as HTMLImageElement).parentElement?.querySelector('.efb') as HTMLElement; if (fb) fb.style.display = 'flex'; }} /><div className="efb absolute inset-0 items-center justify-center overflow-y-auto bg-white rounded-lg m-4" style={{ display: 'none' }}><InstagramEmbed embedHtml={as.embed_html} permalink={as.instagram_url} /></div></div>
              : as.embed_html ? <div className="w-full max-w-md mx-auto bg-white rounded-lg overflow-y-auto max-h-[80vh] p-2"><InstagramEmbed embedHtml={as.embed_html} permalink={as.instagram_url} /></div>
              : <div className="text-center text-white/60"><Instagram size={48} className="mx-auto mb-3 opacity-50" /><a href={as.instagram_url || '#'} target="_blank" rel="noopener noreferrer" className="text-gray-400 flex items-center gap-2 justify-center hover:text-white transition-colors">View on Instagram<ExternalLink size={14} /></a></div>}
              <div className="absolute inset-0 flex z-10"><div className="w-1/3 h-full cursor-pointer" onClick={prev} /><div className="w-1/3 h-full" /><div className="w-1/3 h-full cursor-pointer" onClick={next} /></div>
            </div>
            <div className="absolute bottom-0 left-0 right-0 z-30">
              {as.description && <div className="px-4 pb-2"><button onClick={e => { e.stopPropagation(); setShowCap(s => !s); setPaused(p => (!showCap ? true : p)); }} className="text-left w-full">{showCap ? <div className="bg-black/60 backdrop-blur-sm rounded-xl p-3 max-h-[200px] overflow-y-auto"><p className="text-white text-[13px] whitespace-pre-wrap">{as.description}</p></div> : <p className="text-white/80 text-xs"><span className="line-clamp-1">{as.description.substring(0, 90)}</span>{as.description.length > 90 && <span className="text-white/50 ml-1">...more</span>}</p>}</button></div>}
              <div className="flex items-center justify-between px-4 py-3 bg-gradient-to-t from-black/70 to-transparent"><div className="flex items-center gap-5"><span className="flex items-center gap-1.5 text-white/80"><Heart size={17} /><span className="text-xs font-medium">{fmt(as.like_count)}</span></span>{as.comments_count > 0 && <span className="flex items-center gap-1.5 text-white/80"><MessageCircle size={16} /><span className="text-xs font-medium">{fmt(as.comments_count)}</span></span>}</div>{as.instagram_url && <a href={as.instagram_url} target="_blank" rel="noopener noreferrer" onClick={e => e.stopPropagation()} className="flex items-center gap-1.5 text-white/70 hover:text-white text-xs font-medium">View on IG<ExternalLink size={12} /></a>}</div>
            </div>
          </div>
          <button onClick={prev} disabled={activeIdx === 0} className="hidden md:flex absolute left-4 lg:left-[calc(50%-260px)] top-1/2 -translate-y-1/2 w-10 h-10 bg-white/10 hover:bg-white/20 rounded-full items-center justify-center text-white disabled:opacity-20 z-20"><ChevronLeft size={22} /></button>
          <button onClick={next} disabled={activeIdx === posts.length - 1} className="hidden md:flex absolute right-4 lg:right-[calc(50%-260px)] top-1/2 -translate-y-1/2 w-10 h-10 bg-white/10 hover:bg-white/20 rounded-full items-center justify-center text-white disabled:opacity-20 z-20"><ChevronRight size={22} /></button>
          <div className="hidden md:block absolute bottom-4 left-1/2 -translate-x-1/2 text-white/40 text-xs z-20">{activeIdx + 1}/{posts.length}</div>
        </div>
      )}
    </>
  );
};

/* ═══════════════════════════════════════════════════════════════
   STORIES TAB — Card Grid (actual Instagram stories)
   API: GET  /instastories/stories/:slate_id?active=false&limit=100
   API: POST /instastories/sync-stories/:slate_id
   ═══════════════════════════════════════════════════════════════ */
const StoriesTab: React.FC<{ slateId: string; apiBase: string }> = ({ slateId, apiBase }) => {
  const [activeStories, setActiveStories] = useState<StoryItem[]>([]);
  const [highlights, setHighlights] = useState<StoryItem[]>([]);
  const [archived, setArchived] = useState<StoryItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [igUser, setIgUser] = useState<string | null>(null);
  const [pfpUrl, setPfpUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [viewerItems, setViewerItems] = useState<StoryItem[]>([]);
  const [viewerIdx, setViewerIdx] = useState<number | null>(null);
  const [progress, setProgress] = useState(0);
  const [paused, setPaused] = useState(false);
  const [muted, setMuted] = useState(true);
  const [mediaOk, setMediaOk] = useState(false);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const vidRef = useRef<HTMLVideoElement | null>(null);

  // API already maps: media_url = local_media_path || cdn_media_url
  // So media_url IS the local file path when downloaded. Use it directly.
  // proxy_url only as last fallback (adds extra network hop)
  const getMediaUrl = (s: StoryItem): string | null => {
    if (s.media_type === 'VIDEO') return s.video_url || s.media_url || (s.proxy_url ? `${s.proxy_url}?type=video` : null);
    return s.media_url || s.proxy_url;
  };
  const getThumbUrl = (s: StoryItem): string | null => {
    return s.thumbnail_url || s.media_url || (s.proxy_url ? `${s.proxy_url}?type=thumb` : null);
  };

  const [storyImgErr, setStoryImgErr] = useState<Record<number, boolean>>({});

  const loadStories = useCallback(async () => {
    try {
      setLoading(true); setError(null);
      const res = await fetch(`${apiBase}/instastories/stories/${slateId}?active=false&limit=100`);
      const json = await res.json();
      if (json.success && json.data) {
        setIgUser(json.data.instagram_username || null);
        setPfpUrl(fixUrl(json.data.profile_picture_url, apiBase));
        const all: StoryItem[] = (json.data.stories || []).map((s: StoryItem) => ({
          ...s, media_url: fixUrl(s.media_url, apiBase), video_url: fixUrl(s.video_url, apiBase),
          thumbnail_url: fixUrl(s.thumbnail_url, apiBase), proxy_url: fixUrl(s.proxy_url, apiBase),
        }));
        // Active = not highlight + not expired
        setActiveStories(all.filter(s => !s.is_highlight && s.is_active_on_ig));
        // Highlights = separate section
        setHighlights(all.filter(s => s.is_highlight));
        // Archived = not highlight + expired (so user can still see old stories)
        setArchived(all.filter(s => !s.is_highlight && !s.is_active_on_ig));
      }
    } catch { setError('Failed to load stories'); } finally { setLoading(false); }
  }, [slateId, apiBase]);

  useEffect(() => { loadStories(); }, [loadStories]);
  useEffect(() => { if (success || error) { const t = setTimeout(() => { setSuccess(null); setError(null); }, 10000); return () => clearTimeout(t); } }, [success, error]);

  const handleSync = async () => {
    try { setSyncing(true); setError(null);
      const res = await fetch(`${apiBase}/instastories/sync-stories/${slateId}`, { method: 'POST' });
      const json = await res.json();
      if (json.success) { setSuccess(json.message); await loadStories(); } else setError(json.message);
    } catch { setError('Sync failed'); } finally { setSyncing(false); }
  };

  const clearT = useCallback(() => { if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; } }, []);
  const openViewer = (items: StoryItem[], idx: number) => { setViewerItems(items); setViewerIdx(idx); setProgress(0); setPaused(false); setMediaOk(false); };
  const closeViewer = () => { setViewerIdx(null); setViewerItems([]); clearT(); };
  const nextItem = () => { if (viewerIdx === null) return; if (viewerIdx < viewerItems.length - 1) { setViewerIdx(viewerIdx + 1); setProgress(0); setMediaOk(false); } else closeViewer(); };
  const prevItem = () => { if (viewerIdx === null || viewerIdx <= 0) return; setViewerIdx(viewerIdx - 1); setProgress(0); setMediaOk(false); };

  useEffect(() => { if (viewerIdx === null || paused) { clearT(); return; } clearT(); const s = viewerItems[viewerIdx]; if (!s) return; const dur = s.media_type === 'VIDEO' ? (s.duration_seconds ? s.duration_seconds * 1000 : 15000) : 6000; timerRef.current = setInterval(() => { setProgress(p => { const n = p + (30 / dur) * 100; if (n >= 100) { nextItem(); return 0; } return n; }); }, 30); return clearT; }, [viewerIdx, paused, viewerItems]);
  useEffect(() => { if (viewerIdx === null) return; const h = (e: KeyboardEvent) => { if (e.key === 'Escape') closeViewer(); if (e.key === 'ArrowLeft') prevItem(); if (e.key === 'ArrowRight') nextItem(); if (e.key === ' ') { e.preventDefault(); setPaused(p => !p); } }; window.addEventListener('keydown', h); return () => window.removeEventListener('keydown', h); }, [viewerIdx, viewerItems.length]);
  useEffect(() => { document.body.style.overflow = viewerIdx !== null ? 'hidden' : ''; return () => { document.body.style.overflow = ''; }; }, [viewerIdx]);

  const vs = viewerIdx !== null ? viewerItems[viewerIdx] : null;
  const vsUrl = vs ? getMediaUrl(vs) : null;

  if (loading) return <div className="py-8 flex items-center justify-center gap-3"><Loader2 size={20} className="animate-spin text-gray-900" /><span className="text-sm text-gray-500">Loading stories...</span></div>;
  const totalStories = activeStories.length + highlights.length + archived.length;

  return (
    <>
      <div className="py-5">
        <div className="flex items-center justify-between mb-4 px-1">
          <div className="flex items-center gap-2.5">
            <Film size={18} className="text-gray-900" />
            <h3 className="text-[15px] font-semibold text-gray-900">Stories</h3>
            <span className="text-xs text-gray-400">{activeStories.length} active · {archived.length} archived · {highlights.length} highlights</span>
          </div>
          <button onClick={handleSync} disabled={syncing} className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-white bg-gray-900 rounded-lg hover:bg-gray-800 transition-colors disabled:opacity-50">
            {syncing ? <Loader2 size={13} className="animate-spin" /> : <RefreshCw size={13} />} {syncing ? 'Syncing...' : 'Sync Stories'}
          </button>
        </div>

        {error && <div className="mb-4 p-3 bg-red-50 border-2 border-red-300 rounded-lg flex items-start gap-2"><XCircle size={15} className="text-red-500 mt-0.5" /><p className="text-sm text-red-700">{error}</p></div>}
        {success && <div className="mb-4 p-3 bg-green-50 border-2 border-green-300 rounded-lg flex items-start gap-2"><CheckCircle2 size={15} className="text-green-500 mt-0.5" /><p className="text-sm text-green-700">{success}</p></div>}

        {totalStories === 0 && <div className="py-8 text-center"><div className="w-16 h-16 rounded-2xl mx-auto mb-4 flex items-center justify-center border-2 border-gray-300 bg-transparent"><Film size={28} className="text-gray-400" /></div><p className="text-sm text-gray-500">No Stories Found</p><p className="text-xs text-gray-400 mt-1">Click "Sync Stories" to fetch latest{igUser ? ` from @${igUser}` : ''}</p></div>}

        {activeStories.length > 0 && (
          <div className="mb-6">
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3 px-1">Active Stories ({activeStories.length})</p>
            <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6 gap-3">
              {activeStories.map((s, i) => { const tUrl = getThumbUrl(s); return (
                <button key={s.id} onClick={() => openViewer(activeStories, i)} className="group relative aspect-[9/16] rounded-xl overflow-hidden border-2 border-gray-200 hover:border-gray-900 transition-all bg-gray-50">
                  {!storyImgErr[s.id] && tUrl
                    ? <img src={tUrl} alt="" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" onError={() => setStoryImgErr(p => ({ ...p, [s.id]: true }))} />
                    : <div className="w-full h-full flex items-center justify-center bg-gray-100"><Film size={24} className="text-gray-300" /></div>
                  }
                  {s.media_type === 'VIDEO' && <div className="absolute top-2 right-2 w-6 h-6 bg-white/90 rounded-md flex items-center justify-center shadow-sm border border-gray-200"><Play size={11} className="text-gray-700 ml-[1px]" fill="currentColor" /></div>}
                  <div className="absolute bottom-0 left-0 right-0 px-2 py-1.5 bg-gradient-to-t from-black/60 to-transparent"><span className="text-[10px] text-white/90 font-medium">{timeAgo(s.story_timestamp || s.created_at)}</span></div>
                </button>
              ); })}
            </div>
          </div>
        )}

        {highlights.length > 0 && (() => {
          const groups: Record<string, StoryItem[]> = {};
          highlights.forEach(h => { const key = h.highlight_title || h.highlight_id || 'Highlight'; if (!groups[key]) groups[key] = []; groups[key].push(h); });
          return (<div>
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3 px-1">Highlights</p>
            <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6 gap-3">
              {Object.entries(groups).map(([title, items]) => { const tUrl = getThumbUrl(items[0]); return (
                <button key={title} onClick={() => openViewer(items, 0)} className="group relative aspect-[9/16] rounded-xl overflow-hidden border-2 border-gray-200 hover:border-gray-900 transition-all bg-gray-50">
                  {!storyImgErr[items[0].id] && tUrl
                    ? <img src={tUrl} alt="" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" onError={() => setStoryImgErr(p => ({ ...p, [items[0].id]: true }))} />
                    : <div className="w-full h-full flex items-center justify-center bg-gray-100"><Star size={24} className="text-gray-300" /></div>
                  }
                  <div className="absolute bottom-0 left-0 right-0 px-2.5 py-2 bg-gradient-to-t from-black/70 to-transparent"><p className="text-[11px] text-white font-semibold truncate">{title}</p><p className="text-[10px] text-white/70">{items.length} items</p></div>
                </button>
              ); })}
            </div>
          </div>);
        })()}

        {/* Archived (expired) Stories */}
        {archived.length > 0 && (
          <div className="mt-6">
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3 px-1">Archived ({archived.length})</p>
            <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6 gap-3">
              {archived.map((s, i) => { const tUrl = getThumbUrl(s); return (
                <button key={s.id} onClick={() => openViewer(archived, i)} className="group relative aspect-[9/16] rounded-xl overflow-hidden border-2 border-gray-200 hover:border-gray-900 transition-all bg-gray-50 opacity-70 hover:opacity-100">
                  {!storyImgErr[s.id] && tUrl
                    ? <img src={tUrl} alt="" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" onError={() => setStoryImgErr(p => ({ ...p, [s.id]: true }))} />
                    : <div className="w-full h-full flex items-center justify-center bg-gray-100"><Film size={24} className="text-gray-300" /></div>
                  }
                  {s.media_type === 'VIDEO' && <div className="absolute top-2 right-2 w-6 h-6 bg-white/90 rounded-md flex items-center justify-center shadow-sm border border-gray-200"><Play size={11} className="text-gray-700 ml-[1px]" fill="currentColor" /></div>}
                  <div className="absolute bottom-0 left-0 right-0 px-2 py-1.5 bg-gradient-to-t from-black/60 to-transparent"><span className="text-[10px] text-white/90 font-medium">{timeAgo(s.story_timestamp || s.created_at)}</span></div>
                </button>
              ); })}
            </div>
          </div>
        )}
      </div>

      {/* Fullscreen Story Viewer */}
      {vs && viewerIdx !== null && (
        <div className="fixed inset-0 z-[99999] bg-black/95 flex items-center justify-center">
          <div className="absolute inset-0 hidden md:block" onClick={closeViewer} />
          <div className="relative w-full h-full md:w-[420px] md:h-[94vh] md:max-h-[820px] md:rounded-2xl overflow-hidden bg-black flex flex-col z-10">
            <div className="absolute top-0 left-0 right-0 z-30 flex gap-[3px] px-3 pt-3">{viewerItems.map((_, i) => <div key={i} className="flex-1 h-[2.5px] bg-white/25 rounded-full overflow-hidden"><div className="h-full bg-white rounded-full" style={{ width: i < viewerIdx ? '100%' : i === viewerIdx ? `${progress}%` : '0%' }} /></div>)}</div>
            <div className="absolute top-7 left-0 right-0 z-30 flex items-center justify-between px-4 py-1">
              <div className="flex items-center gap-3"><div className="w-9 h-9 rounded-full border-2 border-white/30 overflow-hidden bg-white/10 flex items-center justify-center">{pfpUrl ? <img src={pfpUrl} alt="" className="w-full h-full object-cover" /> : <span className="text-white text-sm font-bold">{igUser?.charAt(0).toUpperCase() || 'S'}</span>}</div><div><p className="text-white text-sm font-semibold truncate">{igUser ? `@${igUser}` : 'Story'}</p><p className="text-white/60 text-xs">{timeAgo(vs.story_timestamp || vs.created_at)} ago{vs.is_highlight ? ` · ${vs.highlight_title || 'Highlight'}` : ''}</p></div></div>
              <div className="flex items-center gap-1">{vs.media_type === 'VIDEO' && <button onClick={() => { setMuted(m => !m); if (vidRef.current) vidRef.current.muted = !muted; }} className="w-8 h-8 flex items-center justify-center text-white/80 hover:text-white">{muted ? <VolumeX size={18} /> : <Volume2 size={18} />}</button>}<button onClick={() => setPaused(p => !p)} className="w-8 h-8 flex items-center justify-center text-white/80 hover:text-white">{paused ? <Play size={18} /> : <Pause size={18} />}</button><button onClick={closeViewer} className="w-8 h-8 flex items-center justify-center text-white/80 hover:text-white"><X size={20} /></button></div>
            </div>
            <div className="flex-1 flex items-center justify-center relative select-none" onMouseDown={() => setPaused(true)} onMouseUp={() => setPaused(false)} onTouchStart={() => setPaused(true)} onTouchEnd={() => setPaused(false)}>
              {!mediaOk && <div className="absolute inset-0 flex items-center justify-center z-20"><Loader2 size={36} className="text-white/40 animate-spin" /></div>}
              {vs.media_type === 'VIDEO' ? <video ref={vidRef} key={vs.id} src={vsUrl || ''} className="w-full h-full object-contain" autoPlay muted={muted} playsInline onLoadedData={() => setMediaOk(true)} />
              : vsUrl ? <img key={vs.id} src={vsUrl} alt="" className={`w-full h-full object-contain transition-opacity ${mediaOk ? 'opacity-100' : 'opacity-0'}`} onLoad={() => setMediaOk(true)} onError={() => setMediaOk(true)} />
              : <div className="text-center text-white/60"><Film size={48} className="mx-auto mb-3 opacity-50" /><p className="text-sm">Media unavailable</p></div>}
              <div className="absolute inset-0 flex z-10"><div className="w-1/3 h-full cursor-pointer" onClick={prevItem} /><div className="w-1/3 h-full" /><div className="w-1/3 h-full cursor-pointer" onClick={nextItem} /></div>
            </div>
            <div className="absolute bottom-0 left-0 right-0 z-30">
              {vs.caption && <div className="px-4 pb-2"><p className="text-white/80 text-xs line-clamp-2">{vs.caption}</p></div>}
              {vs.link_url && <div className="px-4 pb-2"><a href={vs.link_url} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1.5 text-gray-400 hover:text-white text-xs transition-colors"><ExternalLink size={12} />{vs.link_url.replace(/^https?:\/\//, '').substring(0, 40)}...</a></div>}
              <div className="flex items-center justify-between px-4 py-3 bg-gradient-to-t from-black/70 to-transparent"><span className="text-white/50 text-xs">{viewerIdx + 1}/{viewerItems.length}</span></div>
            </div>
          </div>
          <button onClick={prevItem} disabled={viewerIdx === 0} className="hidden md:flex absolute left-4 lg:left-[calc(50%-260px)] top-1/2 -translate-y-1/2 w-10 h-10 bg-white/10 hover:bg-white/20 rounded-full items-center justify-center text-white disabled:opacity-20 z-20"><ChevronLeft size={22} /></button>
          <button onClick={nextItem} disabled={viewerIdx === viewerItems.length - 1} className="hidden md:flex absolute right-4 lg:right-[calc(50%-260px)] top-1/2 -translate-y-1/2 w-10 h-10 bg-white/10 hover:bg-white/20 rounded-full items-center justify-center text-white disabled:opacity-20 z-20"><ChevronRight size={22} /></button>
        </div>
      )}
    </>
  );
};

/* ═══════════════════════════════════════════════════════════════
   ACCOUNT TAB
   ═══════════════════════════════════════════════════════════════ */
const AccountTab: React.FC<{ slateId: string; apiBase: string; oauthConnected?: boolean }> = ({ slateId, apiBase, oauthConnected }) => {
  const [data, setData] = useState<AccountResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);
  const [addingPost, setAddingPost] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [showDC, setShowDC] = useState(false);
  const [postUrl, setPostUrl] = useState('');
  const [oauthStatus, setOauthStatus] = useState<OAuthStatus | null>(null);
  const [oauthConnecting, setOauthConnecting] = useState(false);
  const [connectUsername, setConnectUsername] = useState('');
  const [connecting, setConnecting] = useState(false);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const api = useCallback(async (path: string, opts?: RequestInit) => (await fetch(`${apiBase}/instastories${path}`, opts)).json(), [apiBase]);
  const load = useCallback(async () => { try { setLoading(true); setError(null); const j = await api(`/account/${slateId}`); if (j.success) setData(j.data); else setError(j.message); } catch { setError('Failed to load'); } finally { setLoading(false); } }, [slateId, api]);

  const loadOAuthStatus = useCallback(async () => {
    try {
      const j = await api(`/oauth/account/${slateId}`);
      if (j.success) setOauthStatus(j.data);
    } catch { /* silent */ }
  }, [slateId, api]);

  useEffect(() => { load(); loadOAuthStatus(); }, [load, loadOAuthStatus]);

  // If parent detected oauth=success callback, reload status immediately
  useEffect(() => { if (oauthConnected) { load(); loadOAuthStatus(); } }, [oauthConnected]);

  useEffect(() => { if (success || error) { const t = setTimeout(() => { setSuccess(null); setError(null); }, 10000); return () => clearTimeout(t); } }, [success, error]);
  useEffect(() => () => { if (pollRef.current) clearInterval(pollRef.current); }, []);

  const sync = async () => { try { setSyncing(true); setError(null); const endpoint = isOAuthConnected ? `/oauth/sync/${slateId}` : `/sync/${slateId}`; const j = await api(endpoint, { method: 'POST' }); if (j.success) { setSuccess(j.message); await load(); await loadOAuthStatus(); } else setError(j.message); } catch { setError('Sync failed'); } finally { setSyncing(false); } };
  const refresh = async () => { try { setRefreshing(true); const j = await api(`/account/${slateId}/refresh`, { method: 'POST' }); if (j.success) { setSuccess(j.message); await load(); } else setError(j.message); } catch { setError('Failed'); } finally { setRefreshing(false); } };
  const addPost = async () => { if (!postUrl.trim()) return; try { setAddingPost(true); setError(null); const j = await api(`/post/${slateId}`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ url: postUrl.trim() }) }); if (j.success) { setSuccess(j.message); setPostUrl(''); await load(); } else setError(j.message); } catch { setError('Failed'); } finally { setAddingPost(false); } };
  const disconnect = async () => { try { setDisconnecting(true); const j = await api(`/account/${slateId}`, { method: 'DELETE' }); if (j.success) { setSuccess('Disconnected'); setShowDC(false); await load(); loadOAuthStatus(); } else setError(j.message); } catch { setError('Failed'); } finally { setDisconnecting(false); } };

  const connectViaOAuth = async () => {
    try {
      setOauthConnecting(true);
      setError(null);
      const j = await api(`/oauth/connect/${slateId}`);
      if (!j.success || !j.data?.auth_url) { setError(j.message || 'Failed to get auth URL'); return; }
      // Open Instagram OAuth in a new tab
      window.open(j.data.auth_url, 'ig_oauth', 'width=600,height=700,left=300,top=100');
      setSuccess('A new tab opened. Log in to Instagram and grant access. This page will update automatically.');
      // Poll for status change every 4 seconds for up to 3 minutes
      let attempts = 0;
      pollRef.current = setInterval(async () => {
        attempts++;
        const statusJ = await api(`/oauth/account/${slateId}`);
        if (statusJ.success && statusJ.data?.status === 'connected') {
          clearInterval(pollRef.current!);
          pollRef.current = null;
          setOauthStatus(statusJ.data);
          await load();
          setSuccess(`@${statusJ.data.username} connected via Official API.`);
          setOauthConnecting(false);
        } else if (attempts >= 45) {
          clearInterval(pollRef.current!);
          pollRef.current = null;
          setOauthConnecting(false);
        }
      }, 4000);
    } catch { setError('Failed'); setOauthConnecting(false); }
  };

  const connectViaPuppeteer = async () => {
    const uname = connectUsername.trim().replace(/^@/, '');
    if (!uname) return;
    try {
      setConnecting(true);
      setError(null);
      const j = await api(`/account/${slateId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username: uname }),
      });
      if (j.success) { setSuccess(j.message || `@${uname} connected!`); setConnectUsername(''); await load(); }
      else setError(j.message || 'Failed to connect');
    } catch { setError('Connection failed'); }
    finally { setConnecting(false); }
  };

  if (loading) return <div className="bg-white rounded-xl border-2 border-gray-200 p-6"><div className="h-20 bg-gray-50 rounded-lg animate-pulse" /></div>;

  const acct = data?.account;
  const isConn = data?.is_connected || false;
  const posts = data?.posts_count || 0;
  const picUrl = acct?.profile_picture_url ? fixUrl(acct.profile_picture_url, apiBase) : null;
  const isOAuthConnected = oauthStatus?.status === 'connected';
  const isOAuthExpired   = oauthStatus?.status === 'expired';


  return (
    <div className="space-y-4">

      {/* ── Official API connection card ── */}
      <div className={`bg-white rounded-xl border-2 overflow-hidden ${isOAuthConnected ? 'border-green-300' : isOAuthExpired ? 'border-amber-300' : 'border-gray-200'}`}>
        <div className="px-5 py-4 border-b border-gray-100 flex items-center gap-3">
          <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${isOAuthConnected ? 'bg-green-50' : 'bg-gray-50'}`}>
            <ShieldCheck size={18} className={isOAuthConnected ? 'text-green-600' : 'text-gray-400'} />
          </div>
          <div className="flex-1">
            <h3 className="text-[14px] font-semibold text-gray-900">Official Instagram API</h3>
            <p className="text-[11px] text-gray-500">Secure OAuth — no scraping, no ToS risk</p>
          </div>
          {isOAuthConnected && (
            <span className="flex items-center gap-1 text-[11px] font-semibold text-green-700 bg-green-50 px-2 py-1 rounded-full">
              <CheckCircle2 size={10} /> Active
            </span>
          )}
          {isOAuthExpired && (
            <span className="flex items-center gap-1 text-[11px] font-semibold text-amber-700 bg-amber-50 px-2 py-1 rounded-full">
              <AlertTriangle size={10} /> Expired
            </span>
          )}
        </div>

        {error && <div className="mx-5 mt-4 flex items-start gap-2 p-3 bg-red-50 border-2 border-red-200 rounded-lg"><XCircle size={15} className="text-red-500 mt-0.5 shrink-0" /><p className="text-sm text-red-700">{error}</p></div>}
        {success && <div className="mx-5 mt-4 flex items-start gap-2 p-3 bg-green-50 border-2 border-green-200 rounded-lg"><CheckCircle2 size={15} className="text-green-500 mt-0.5 shrink-0" /><p className="text-sm text-green-700">{success}</p></div>}

        <div className="p-5">
          {isOAuthConnected ? (
            <div>
              {/* Account info */}
              <div className="flex items-center gap-3 mb-4">
                <div className="text-sm text-gray-700">
                  Connected as <span className="font-semibold">@{oauthStatus?.username}</span>
                </div>
              </div>

              {/* Account Stats */}
              <div className="grid grid-cols-2 gap-3 mb-4">
                <div className="hidden">
                  <Users size={16} className="text-gray-400 mx-auto mb-1.5" />
                  <p className="text-lg font-semibold">{fmt(oauthStatus?.followers_count)}</p>
                  <p className="text-[11px] text-gray-500">Followers</p>
                </div>
                <div className="text-center p-3 bg-white rounded-lg border-2 border-gray-200">
                  <Image size={16} className="text-gray-400 mx-auto mb-1.5" />
                  <p className="text-lg font-semibold">{oauthStatus?.posts_count || 0}</p>
                  <p className="text-[11px] text-gray-500">Stored Posts</p>
                </div>
                <div className="text-center p-3 bg-white rounded-lg border-2 border-gray-200">
                  <Clock size={16} className="text-gray-400 mx-auto mb-1.5" />
                  <p className="text-xs font-medium mt-1">{fmtDate(oauthStatus?.last_synced_at ?? null)}</p>
                  <p className="text-[11px] text-gray-500">Last Synced</p>
                </div>
              </div>

              {/* Insights (last 28 days) */}
              {false && oauthStatus?.insights && (
                <div className="mb-4">
                  <p className="text-[11px] font-semibold text-gray-500 uppercase tracking-wide mb-2">Insights — Last 28 Days</p>
                  <div className="grid grid-cols-3 gap-2">
                    <div className="text-center p-3 bg-blue-50 rounded-lg border-2 border-blue-100">
                      <Film size={16} className="text-blue-500 mx-auto mb-1.5" />
                      <p className="text-lg font-semibold text-blue-700">{fmt(oauthStatus?.insights?.reach)}</p>
                      <p className="text-[11px] text-blue-500">Reach</p>
                    </div>
                    <div className="text-center p-3 bg-green-50 rounded-lg border-2 border-green-100">
                      <Users size={16} className="text-green-500 mx-auto mb-1.5" />
                      <p className="text-lg font-semibold text-green-700">{fmt(oauthStatus?.insights?.profile_views)}</p>
                      <p className="text-[11px] text-green-500">Profile Views</p>
                    </div>
                    <div className="text-center p-3 bg-pink-50 rounded-lg border-2 border-pink-100">
                      <Heart size={16} className="text-pink-500 mx-auto mb-1.5" />
                      <p className="text-lg font-semibold text-pink-700">{fmt(oauthStatus?.insights?.likes)}</p>
                      <p className="text-[11px] text-pink-500">Likes</p>
                    </div>
                    <div className="text-center p-3 bg-purple-50 rounded-lg border-2 border-purple-100">
                      <MessageCircle size={16} className="text-purple-500 mx-auto mb-1.5" />
                      <p className="text-lg font-semibold text-purple-700">{fmt(oauthStatus?.insights?.comments)}</p>
                      <p className="text-[11px] text-purple-500">Comments</p>
                    </div>
                    <div className="text-center p-3 bg-orange-50 rounded-lg border-2 border-orange-100">
                      <ExternalLink size={16} className="text-orange-500 mx-auto mb-1.5" />
                      <p className="text-lg font-semibold text-orange-700">{fmt(oauthStatus?.insights?.shares)}</p>
                      <p className="text-[11px] text-orange-500">Shares</p>
                    </div>
                    <div className="text-center p-3 bg-yellow-50 rounded-lg border-2 border-yellow-100">
                      <Star size={16} className="text-yellow-600 mx-auto mb-1.5" />
                      <p className="text-lg font-semibold text-yellow-700">{fmt(oauthStatus?.insights?.saves)}</p>
                      <p className="text-[11px] text-yellow-600">Saves</p>
                    </div>
                  </div>
                </div>
              )}

              {/* Sync Now */}
              <div className="p-3 bg-white border-2 border-gray-900 rounded-xl mb-4 flex items-center justify-between">
                <div className="flex items-center gap-2.5">
                  <div className="w-7 h-7 bg-gray-900 rounded-lg flex items-center justify-center"><RefreshCw size={14} className="text-white" /></div>
                  <p className="text-sm font-semibold text-gray-900">Fetch Latest Posts</p>
                </div>
                <button onClick={sync} disabled={syncing} className="flex items-center gap-1.5 px-4 py-2 text-sm font-semibold text-white bg-gray-900 hover:bg-gray-800 rounded-lg disabled:opacity-50 transition-colors">
                  {syncing ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />}
                  {syncing ? 'Syncing...' : 'Sync Now'}
                </button>
              </div>

            </div>
          ) : isOAuthExpired ? (
            <div>
              <p className="text-sm text-amber-700 mb-3">Token expired for @{oauthStatus?.username}. Reconnect to restore access.</p>
              <button
                onClick={connectViaOAuth}
                disabled={oauthConnecting}
                className="flex items-center gap-2 px-4 py-2.5 text-sm font-semibold text-white bg-gray-900 hover:bg-gray-800 rounded-xl disabled:opacity-50 transition-colors"
              >
                {oauthConnecting ? <Loader2 size={15} className="animate-spin" /> : <Instagram size={15} />}
                {oauthConnecting ? 'Waiting for connection...' : 'Reconnect via Instagram'}
              </button>
            </div>
          ) : isConn && acct ? (
            <div>
              {/* Existing connection info */}
              <div className="flex items-center gap-4 p-4 bg-gray-50 rounded-xl mb-4 border border-gray-200">
                <div className="relative">
                  <div className="w-14 h-14 rounded-xl overflow-hidden border-2 border-gray-900 shadow-sm">
                    {picUrl ? <img src={picUrl} alt="" className="w-full h-full object-cover" onError={e => { (e.target as HTMLImageElement).style.display = 'none'; }} /> : <div className="w-full h-full flex items-center justify-center bg-gray-100"><span className="text-gray-900 font-bold text-lg">{acct.username?.charAt(0).toUpperCase()}</span></div>}
                  </div>
                  <div className="absolute -bottom-1 -right-1 w-5 h-5 bg-green-500 rounded-full border-2 border-white flex items-center justify-center"><CheckCircle2 size={10} className="text-white" /></div>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2"><p className="text-[15px] font-semibold text-gray-900 truncate">@{acct.username}</p><a href={`https://instagram.com/${acct.username}`} target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-gray-900 transition-colors"><ExternalLink size={13} /></a></div>
                  <span className="text-xs text-gray-500">{posts} posts stored</span>
                </div>
                <button onClick={refresh} disabled={refreshing} className="p-2 text-gray-400 hover:text-gray-900 rounded-lg disabled:opacity-50 transition-colors">{refreshing ? <Loader2 size={15} className="animate-spin" /> : <RefreshCw size={15} />}</button>
              </div>
              <div className="grid grid-cols-3 gap-3 mb-4">
                <div className="text-center p-3 bg-white rounded-lg border-2 border-gray-200"><Users size={16} className="text-gray-400 mx-auto mb-1.5" /><p className="text-lg font-semibold">{fmt(acct.followers_count)}</p><p className="text-[11px] text-gray-500">Followers</p></div>
                <div className="text-center p-3 bg-white rounded-lg border-2 border-gray-200"><Image size={16} className="text-gray-400 mx-auto mb-1.5" /><p className="text-lg font-semibold">{posts}</p><p className="text-[11px] text-gray-500">Stored</p></div>
                <div className="text-center p-3 bg-white rounded-lg border-2 border-gray-200"><Clock size={16} className="text-gray-400 mx-auto mb-1.5" /><p className="text-xs font-medium mt-1">{fmtDate(acct.last_synced_at)}</p><p className="text-[11px] text-gray-500">Last Synced</p></div>
              </div>
              <div className="p-3 bg-white border-2 border-gray-900 rounded-xl mb-4 flex items-center justify-between">
                <div className="flex items-center gap-2.5"><div className="w-7 h-7 bg-gray-900 rounded-lg flex items-center justify-center"><RefreshCw size={14} className="text-white" /></div><p className="text-sm font-semibold text-gray-900">Fetch Latest Posts</p></div>
                <button onClick={sync} disabled={syncing} className="flex items-center gap-1.5 px-4 py-2 text-sm font-semibold text-white bg-gray-900 hover:bg-gray-800 rounded-lg disabled:opacity-50 transition-colors">{syncing ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />} {syncing ? 'Syncing...' : 'Sync Now'}</button>
              </div>
              <div className="p-3 bg-gray-50 rounded-xl mb-4 border-2 border-gray-200">
                <p className="text-xs font-semibold text-gray-600 mb-2 flex items-center gap-1.5"><Link2 size={12} /> Add Post by URL</p>
                <div className="flex gap-2">
                  <input type="text" value={postUrl} onChange={e => setPostUrl(e.target.value)} placeholder="https://instagram.com/p/... or /reel/..." className="flex-1 pl-3 pr-3 py-2 text-sm border-2 border-gray-200 rounded-lg focus:outline-none focus:border-gray-900 bg-white transition-colors" onKeyDown={e => { if (e.key === 'Enter') addPost(); }} />
                  <button onClick={addPost} disabled={addingPost || !postUrl.trim()} className="px-3 py-2 text-sm font-medium text-white bg-gray-900 hover:bg-gray-800 rounded-lg disabled:opacity-40 flex items-center gap-1.5 transition-colors">{addingPost ? <Loader2 size={13} className="animate-spin" /> : <Plus size={13} />} Add</button>
                </div>
              </div>
              {!showDC ? (
                <button onClick={() => setShowDC(true)} className="w-full flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-medium text-red-600 bg-white border-2 border-red-300 hover:border-red-500 rounded-lg transition-colors mb-4"><Unlink size={15} /> Disconnect</button>
              ) : (
                <div className="p-3 bg-red-50 border-2 border-red-300 rounded-lg mb-4">
                  <p className="text-sm text-red-700 mb-3">Disconnect @{acct.username}?</p>
                  <div className="flex gap-2">
                    <button onClick={disconnect} disabled={disconnecting} className="flex-1 flex items-center justify-center gap-2 px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-lg disabled:opacity-50 transition-colors">{disconnecting ? <Loader2 size={15} className="animate-spin" /> : <Unlink size={15} />} Yes</button>
                    <button onClick={() => setShowDC(false)} className="px-4 py-2 text-sm text-gray-600 bg-white border-2 border-gray-200 rounded-lg hover:border-gray-400 transition-colors">Cancel</button>
                  </div>
                </div>
              )}
              {/* Upgrade to Official API banner — hidden until Meta App Review is approved */}
            </div>
          ) : (
            <div>
              <p className="text-sm text-gray-500 mb-4">Enter the school's Instagram username to connect and sync posts automatically.</p>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={connectUsername}
                  onChange={e => setConnectUsername(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter') connectViaPuppeteer(); }}
                  placeholder="@schoolusername"
                  className="flex-1 pl-3 pr-3 py-2.5 text-sm border-2 border-gray-200 rounded-xl focus:outline-none focus:border-gray-900 bg-white transition-colors"
                />
                <button
                  onClick={connectViaPuppeteer}
                  disabled={connecting || !connectUsername.trim()}
                  className="flex items-center gap-2 px-4 py-2.5 text-sm font-semibold text-white bg-gray-900 hover:bg-gray-800 rounded-xl disabled:opacity-50 transition-colors"
                >
                  {connecting ? <Loader2 size={15} className="animate-spin" /> : <Instagram size={15} />}
                  {connecting ? 'Connecting...' : 'Connect'}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

    </div>
  );
};
